import React, { useEffect, useState } from "react";
import { useLang } from "@/context/LangContext";
import api from "@/lib/api";
import { Award, Sparkles } from "lucide-react";
import { format, parseISO } from "date-fns";

export default function AchievementsSection() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);

  useEffect(() => { (async () => {
    try { const { data } = await api.get(`/achievements?limit=40`); setItems(data); } catch(e){}
  })(); }, []);

  const weekly = items.filter((a) => a.period === "weekly");
  const special = items.filter((a) => a.period === "special");

  if (items.length === 0) return null;

  return (
    <section id="achievements" data-testid="achievements-section" className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32 border-t border-[hsl(var(--border))]">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
        <div>
          <p className="text-xs tracking-[0.25em] text-[hsl(var(--muted-foreground))]">{t.achievements.eyebrow}</p>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight font-medium mt-3">{t.achievements.title}</h2>
        </div>
        <p className="text-sm text-[hsl(var(--muted-foreground))] max-w-md">{t.achievements.sub}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Weekly awards */}
        <div data-testid="achievements-weekly" className="lg:col-span-7 rounded-2xl border border-[hsl(var(--border))] bg-white p-6 md:p-8">
          <div className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[hsl(var(--secondary))]">
            <Award className="w-4 h-4" />{t.achievements.weekly_eyebrow}
          </div>
          <h3 className="font-display text-2xl tracking-tight font-medium mt-2">{t.achievements.weekly_title}</h3>
          {weekly.length === 0 ? (
            <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">{t.achievements.empty}</p>
          ) : (
            <ul className="mt-6 divide-y divide-[hsl(var(--border))]/70" data-testid="achievements-weekly-list">
              {weekly.map((a) => (
                <li key={a.id} data-testid={`achievement-${a.id}`} className="py-4 grid grid-cols-1 md:grid-cols-12 gap-3">
                  <div className="md:col-span-3 text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">
                    {a.week_of ? `Week of ${format(parseISO(a.week_of), "MMM dd")}` : format(parseISO(a.created_at), "MMM dd, yyyy")}
                  </div>
                  <div className="md:col-span-9">
                    <h4 className="font-display text-lg tracking-tight">{a.student_name}</h4>
                    <p className="text-sm text-[hsl(var(--primary))] font-medium">{lang === "zh" && a.award_type_zh ? a.award_type_zh : a.award_type}</p>
                    {a.note && <p className="mt-1 text-xs text-[hsl(var(--muted-foreground))] italic">"{a.note}"</p>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Special awards */}
        <div data-testid="achievements-special" className="lg:col-span-5 rounded-2xl border border-[hsl(var(--primary))]/30 bg-gradient-to-br from-[hsl(var(--primary))]/5 to-transparent p-6 md:p-8 relative overflow-hidden">
          <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-[hsl(var(--primary))]/10 blur-2xl" />
          <div className="relative">
            <div className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[hsl(var(--primary))]">
              <Sparkles className="w-4 h-4" />{t.achievements.special_eyebrow}
            </div>
            <h3 className="font-display text-2xl tracking-tight font-medium mt-2">{t.achievements.special_title}</h3>
            {special.length === 0 ? (
              <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">{t.achievements.empty}</p>
            ) : (
              <ul className="mt-6 space-y-4" data-testid="achievements-special-list">
                {special.map((a) => (
                  <li key={a.id} data-testid={`achievement-${a.id}`} className="rounded-xl bg-white/80 backdrop-blur border border-[hsl(var(--border))] p-4">
                    <p className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--primary))]">{lang === "zh" && a.award_type_zh ? a.award_type_zh : a.award_type}</p>
                    <h4 className="font-display text-xl tracking-tight font-medium mt-1">{a.student_name}</h4>
                    {a.note && <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{a.note}</p>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
